import styled, { css, type RuleSet } from 'styled-components';

import { FONTS, COLORS, BREAKPOINTS } from '~/src/common/theme';
import { getMediaQuery } from '~/src/common/theme/deprecated';

import type { DesktopLayout, MobileLayout, Visibility } from './types';

// Petit alias pour ce qui est autorisé pour le CSS
type CSS = RuleSet | string;

/**
 * Permet de changer la visibilité d'un élément afin par exemple de
 * ne l'afficher que pour les utilisateurs utilisant un petit écran.
 */
const Visibilities: Record<Visibility, CSS> = {
  all: '',
  none: css`
    display: none !important;
  `,
  mobile: css`
    @media ${getMediaQuery(BREAKPOINTS.XL, null)} {
      display: none !important;
    }
  `,
  desktop: css`
    @media ${getMediaQuery(0, BREAKPOINTS.XL)} {
      display: none !important;
    }
  `,
};

/** Le titre d'un menu, avec une visibilité configurable */
export const Title = styled.strong<{ $visibility?: Visibility }>`
  ${({ $visibility }) => Visibilities[$visibility ?? 'all']}
`;

/** Une entrée de menu, avec une visibilité configurable */
export const Entry = styled.span<{ $visibility?: Visibility }>`
  ${({ $visibility }) => Visibilities[$visibility ?? 'all']}
`;

/**
 * Les différents layout disponibles pour le mobile. Ils sont affichés
 * horizontalement et scrollable afin d'accèder à l'ensemble des entrées.
 */
const MobileLayouts: Record<MobileLayout, CSS> = {
  // Menu principal, affiché sur fond vert mon-marché
  primary: css`
    gap: 8px;
    padding: 8px;
    background: ${COLORS.NEUTRAL_DEFAULT};

    > ${Title} {
      display: none;
    }

    > ${Entry} {
      padding: 4px 8px;
      border-radius: 4px;

      font-size: 14px;
      line-height: 16px;
      color: ${COLORS.NEUTRAL_WHITE};
      background: ${COLORS.NEUTRAL_DEFAULT};

      &[aria-current='true'] {
        color: ${COLORS.NEUTRAL_WHITE};
        background: ${COLORS.ACTION_DEFAULT};
      }
    }
  `,
  // Utilisé pour les sous-catégories, sur fond crème
  secondary: css`
    gap: 8px;
    padding: 8px;
    background: ${COLORS.GROUND_UNDERGROUNDLIGHT};

    > ${Title} {
      display: none;
    }

    > ${Entry} {
      padding: 4px 8px;
      border-radius: 4px;

      font-size: 12px;
      line-height: 16px;
      color: ${COLORS.NEUTRAL_DEFAULT};
      background: ${COLORS.NEUTRAL_WHITE};

      &[aria-current='true'] {
        color: ${COLORS.NEUTRAL_WHITE};
        background: ${COLORS.ACTION_DEFAULT};
      }
    }
  `,
};

/**
 * Les différents layout disponibles pour le desktop. De simples menu
 * verticaux, les éléments sont affichés les uns en dessous des autres.
 */
const DesktopLayouts: Record<DesktopLayout, CSS> = {
  // Menu princpal, les entrées, si marquées comme non-expanded, vont
  // automatiquement masquer tout sous-menu placées directement après.
  primary: css`
    gap: 12px;
    padding: 16px;

    > ${Title} {
      font-size: 20px;
      font-weight: 700;
      font-family: ${FONTS.RETHINK};
      color: ${COLORS.NEUTRAL_DEFAULT};
    }

    > ${Entry} {
      margin-left: 8px;

      font-size: 16px;
      font-weight: 600;
      line-height: 24px;
      color: ${COLORS.NEUTRAL_DEFAULT};

      &[href] {
        &:hover {
          color: ${COLORS.ACTION_HOVER};
        }
      }

      &[aria-expanded] {
        &::after {
          content: '\\e960';

          font-size: 24px;
          font-family: ${FONTS.ICONS};
          color: ${COLORS.ACTION_DEFAULT};

          margin-left: 8px;
          display: inline-block;
          vertical-align: bottom;

          transform: translateY(2px);
          transition: transform 0.3s ease;
        }

        &[aria-expanded='true']::after {
          transform: scaleY(-1);
        }

        &[aria-expanded='false'] + nav {
          display: none;
        }
      }
    }
  `,
  // Menu secondaire, principalement pour les sous-catégories
  // Les entrées actives sont marquées par un point à leur gauche
  secondary: css`
    gap: 8px;
    margin-left: 8px;
    align-items: start;

    > ${Title} {
      font-weight: bold;
    }

    > ${Entry} {
      display: block;
      margin-left: 8px;
      padding-left: 16px;

      max-width: calc(100% - 8px); /* Pour le margin-left */
      position: relative;

      font-size: 14px;
      font-weight: 400;
      line-height: 24px;
      color: ${COLORS.NEUTRAL_DEFAULT};

      &[href]:hover {
        color: ${COLORS.ACTION_HOVER};
      }

      &::before {
        content: '';

        position: absolute;
        inset: auto auto 50% 0;

        display: block;
        width: 8px;
        height: 8px;

        translate: 0 50%;
        border-radius: 50%;
        background: ${COLORS.ACTION_DEFAULT};

        scale: 0;
        transition: scale 0.2s ease;
      }

      &[aria-current='true'] {
        font-weight: 500;

        &::before {
          scale: 1;
        }
      }
    }
  `,
};

export const Menu = styled.nav<{
  $mobile: MobileLayout;
  $desktop: DesktopLayout;
  $visibility?: Visibility;
}>`
  ${({ $visibility, $mobile, $desktop }) => css`
    ${Visibilities[$visibility ?? 'all']}

    ${Title}, ${Entry} {
      flex: 0 0 auto;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    @media ${getMediaQuery(0, BREAKPOINTS.XL)} {
      display: flex;
      overflow: auto;
      flex-flow: row nowrap;
      scrollbar-width: none;
      scroll-behavior: smooth;
      overscroll-behavior: contain;
      ${MobileLayouts[$mobile]};
    }

    @media ${getMediaQuery(BREAKPOINTS.XL, null)} {
      display: flex;
      overflow: hidden;
      flex-direction: column;
      ${DesktopLayouts[$desktop]};
    }
  `}
`;
